import { useCallback, useState } from 'react'

import {
  FetchTrainingsScormPackageThumbnailCandidatesDocument,
  FetchTrainingsScormPackageThumbnailCandidatesMutation,
  PurgeTrainingsScormPackageThumbnailCandidatesDocument,
  PurgeTrainingsScormPackageThumbnailCandidatesMutation,
  SetTrainingsScormPackageThumbnailDocument,
  SetTrainingsScormPackageThumbnailMutation,
  TrainingThumbnailDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface UseScormThumbnailPickerParams {
  scormPackageId: string | undefined
  trainingId: string
  onThumbnailSet?: () => void
}

export const useScormThumbnailPicker = ({
  scormPackageId,
  trainingId,
  onThumbnailSet,
}: UseScormThumbnailPickerParams) => {
  const [candidateUrls, setCandidateUrls] = useState<string[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [thumbnailSet, setThumbnailSet] = useState(false)

  const [fetchCandidates, { loading: loadingCandidates }] = useMutation(
    FetchTrainingsScormPackageThumbnailCandidatesDocument,
    {
      extractorFn: (data: FetchTrainingsScormPackageThumbnailCandidatesMutation) =>
        data.trainingsMutations.fetchThumbnailCandidatesTrainingsScormPackage,
    }
  )

  const [setThumbnail, { loading: settingThumbnail }] = useMutation(
    SetTrainingsScormPackageThumbnailDocument,
    {
      extractorFn: (data: SetTrainingsScormPackageThumbnailMutation) =>
        data.trainingsMutations.setThumbnailTrainingsScormPackage,
      refetchQueries: [{ query: TrainingThumbnailDocument, variables: { trainingId } }],
    }
  )

  const [purgeCandidates] = useMutation(PurgeTrainingsScormPackageThumbnailCandidatesDocument, {
    extractorFn: (data: PurgeTrainingsScormPackageThumbnailCandidatesMutation) =>
      data.trainingsMutations.purgeThumbnailCandidatesTrainingsScormPackage,
  })

  const openPicker = useCallback(async () => {
    if (loadingCandidates) return
    if (!scormPackageId) {
      setError(i18n.t('trainings.scorm.thumbnail.error_loading'))
      return
    }
    setError(null)

    const result = await fetchCandidates({
      variables: { id: scormPackageId },
    })

    const handled = handleResult(result, () => {
      setError(i18n.t('trainings.scorm.thumbnail.error_loading'))
    })

    if (handled.success) {
      const urls = handled.data.scormPackage?.thumbnailCandidateUrls ?? []
      if (urls.length === 0) {
        setError(i18n.t('trainings.scorm.thumbnail.no_candidates'))
        return
      }
      setCandidateUrls(urls)
      setIsOpen(true)
    }
  }, [fetchCandidates, loadingCandidates, scormPackageId])

  const pickThumbnail = useCallback(
    async (index: number) => {
      if (!scormPackageId) {
        setError(i18n.t('trainings.scorm.thumbnail.error_setting'))
        return
      }
      setError(null)

      const result = await setThumbnail({
        variables: { id: scormPackageId, candidateIndex: index },
      })

      const handled = handleResult(result, () => {
        setError(i18n.t('trainings.scorm.thumbnail.error_setting'))
      })

      if (handled.success) {
        setIsOpen(false)
        setCandidateUrls([])
        setThumbnailSet(true)
        onThumbnailSet?.()
      }
    },
    [setThumbnail, scormPackageId, onThumbnailSet]
  )

  const closePicker = useCallback(() => {
    setIsOpen(false)
    setError(null)
    if (scormPackageId && candidateUrls.length > 0 && !settingThumbnail) {
      setCandidateUrls([])
      void purgeCandidates({ variables: { id: scormPackageId } })
    }
  }, [candidateUrls.length, settingThumbnail, purgeCandidates, scormPackageId])

  return {
    openPicker,
    closePicker,
    pickThumbnail,
    candidateUrls,
    isOpen,
    error,
    loadingCandidates,
    settingThumbnail,
    thumbnailSet,
  }
}
